/* eslint-disable @typescript-eslint/no-explicit-any */
import { Node, Edge, MarkerType } from 'reactflow';
import { SelectedEntity } from '../BgpVisibilityData';
import { isNotInjected, prefixIdentityKey, RouteRow } from './routeModel';
import { BGP_EDGE_COLORS } from '../styles/bgpVisibilityStyles';
import { compareNeighborIp, type DownNeighbor } from '../../../helpers/bgpValidation';

/**
 * Neighbor cards are compact (a prefix COUNT, not a per-prefix list) — the full prefix
 * list is shown on click. This keeps every neighbor node a fixed, bounded size regardless
 * of announcement volume (no overlap) and lets edges be aggregated per route-table with a
 * count label instead of one edge per prefix.
 */
export const TOPOLOGY_CARD_LAYOUT = {
  /** Upper-bound height of a compact neighbor card (the card's min-height matches this, so
   *  positioning never undershoots the rendered DOM and cards never overlap). */
  neighborCardH: 170,
  /** Vertical gap between neighbor cards. */
  cardGap: 80
};

interface GenerateGraphParams {
  /** Filtered, normalized route rows. In cluster mode this holds EVERY member's rows so the
   *  graph can distinguish the selected reflector from the secondary one. */
  rows: RouteRow[];
  direction: 'INBOUND' | 'OUTBOUND';
  selectedEntity: SelectedEntity | null;
  /** Cluster mode (CTX-106): the set of destination keys (route-table id inbound, subnet id
   *  outbound) where the selected reflector and the other cluster member announce a DIFFERENT
   *  number of prefixes. Edges to these destinations get a yellow `×N` count badge (line/arrow
   *  stay pink/grey). Omit / empty for single-reflector mode or when everything is consistent. */
  inconsistentRouteTables?: Set<string>;
  /** Peers reported down that carry no routes, from `deriveDownNeighborsWithoutRoutes`. Drawn as
   *  route-less nodes so a down session is visible instead of silently absent. Already excludes
   *  peers present in `rows`, so it can never duplicate a node. */
  downNeighbors?: DownNeighbor[];
}

interface DisplayPrefix {
  neighbor_ip: string;
  prefix: string;
  family: string;
  local_pref: number;
  med: number;
  as_path: number[];
  vpcs: Set<string>;
  rtCount: number;
  isBestPath: boolean;
  withdrawn: boolean;
}

interface NeighborAgg {
  prefixes: Map<string, DisplayPrefix>;
  routeTables: Set<string>;
}

interface EdgeAgg {
  neighborIp: string;
  handleKey: string;
  prefixes: Set<string>;
  /** True when any prefix on this (neighbor, route-table) edge is a best path → pink, else grey. */
  best: boolean;
  /** Which members announce this (neighbor, route-table) edge (kept for the edge `data` payload). */
  reflectors: Set<string>;
}

const PINK = BGP_EDGE_COLORS.selected;
const GREY = BGP_EDGE_COLORS.secondary;
const WARNING = BGP_EDGE_COLORS.inconsistent;

export const generateGraphNodesAndEdges = ({
  rows,
  direction,
  selectedEntity,
  inconsistentRouteTables,
  downNeighbors
}: GenerateGraphParams): { nodes: Node[]; edges: Edge[] } => {
  const newNodes: Node[] = [];
  const newEdges: Edge[] = [];

  const routeRows = rows ?? [];
  const downPeers = downNeighbors ?? [];

  // Down peers alone are enough to draw: when every session is down there are no rows at all, and
  // that is precisely the case the operator most needs to see rendered rather than blank.
  if (routeRows.length === 0 && downPeers.length === 0) {
    return { nodes: newNodes, edges: newEdges };
  }

  const isOutbound = direction === 'OUTBOUND';

  const neighborAgg = new Map<string, NeighborAgg>();
  const edgeAgg = new Map<string, EdgeAgg>();
  const uniqueVpcs = new Set<string>();

  // Single pass: aggregate per neighbor (for the compact card) and per (neighbor, route-table)
  // (for the summarized edges). No per-prefix nodes/edges/handles are produced.
  for (const row of routeRows) {
    // Withdrawn (tombstone) prefixes are audit-only: they stay in the table but are never drawn
    // on the topology graph — no node, no edge, and their `vpc: 'withdrawn'` never pollutes the
    // cloud container (CTX-72). A not-injected row is the same kind of audit-only record: there
    // is no route in that table to draw an edge to (CTX-738).
    if (row.withdrawn || isNotInjected(row)) continue;
    uniqueVpcs.add(row.vpc);

    let na = neighborAgg.get(row.neighborIp);
    if (!na) {
      na = { prefixes: new Map(), routeTables: new Set() };
      neighborAgg.set(row.neighborIp, na);
    }
    const pk = prefixIdentityKey(row);
    const existing = na.prefixes.get(pk);
    if (existing) {
      existing.rtCount += 1;
      existing.isBestPath = existing.isBestPath || row.isBestPath;
      existing.withdrawn = existing.withdrawn || row.withdrawn;
      existing.vpcs.add(row.vpc);
    } else {
      na.prefixes.set(pk, {
        neighbor_ip: row.neighborIp,
        prefix: row.prefix,
        family: row.family,
        local_pref: row.localPref,
        med: row.med,
        as_path: row.asPath,
        vpcs: new Set([row.vpc]),
        rtCount: 1,
        isBestPath: row.isBestPath,
        withdrawn: row.withdrawn
      });
    }
    na.routeTables.add(row.routeTableId);

    const handleKey = isOutbound ? (row.subnetId || row.routeTableId) : row.routeTableId;
    if (handleKey) {
      const ek = `${row.neighborIp}::${handleKey}`;
      let ea = edgeAgg.get(ek);
      if (!ea) {
        ea = { neighborIp: row.neighborIp, handleKey, prefixes: new Set(), best: false, reflectors: new Set() };
        edgeAgg.set(ek, ea);
      }
      ea.prefixes.add(pk);
      if (row.reflectorId) ea.reflectors.add(row.reflectorId);
      if (row.isBestPath) {
        ea.best = true;
      }
    }
  }

  // Compact neighbor nodes (fixed height, prefix COUNT + full list for the on-click detail).
  //
  // Emitted in IP order, NOT `neighborAgg` insertion order: insertion order follows the wire, and
  // the /visibility payload does not order `neighbors[]` consistently across cluster members. That
  // made the cards jump vertically when switching PRIMARY ↔ SECONDARY, reading as a topology
  // difference where there was none. Sorting here (not at the aggregation) keeps the single pass
  // above untouched.
  let currentY = 50;
  const orderedNeighbors = Array.from(neighborAgg.entries()).sort(([a], [b]) => compareNeighborIp(a, b));
  for (const [neighborIp, na] of orderedNeighbors) {
    const prefixesArr = Array.from(na.prefixes.values());
    newNodes.push({
      id: `neighbor-${neighborIp}`,
      type: 'neighbor',
      position: { x: 50, y: currentY },
      data: {
        neighborIp,
        prefixCount: prefixesArr.length,
        routeTableCount: na.routeTables.size,
        bestCount: prefixesArr.filter((p) => p.isBestPath).length,
        withdrawnCount: prefixesArr.filter((p) => p.withdrawn).length,
        // Full per-prefix list, consumed by the neighbor modal when the card is clicked.
        prefixes: prefixesArr.map((p) => ({
          neighbor_ip: p.neighbor_ip,
          prefix: p.prefix,
          family: p.family,
          local_pref: p.local_pref,
          med: p.med,
          as_path: p.as_path,
          vpcCount: p.vpcs.size,
          rtCount: p.rtCount,
          isBestPath: p.isBestPath,
          withdrawn: p.withdrawn
        }))
      }
    });
    currentY += TOPOLOGY_CARD_LAYOUT.neighborCardH + TOPOLOGY_CARD_LAYOUT.cardGap;
  }

  // Down peers last, below the peers that are actually exchanging routes: healthy sessions stay
  // at the top of the column where the eye lands first, and the down ones read as a group.
  // They get no edges — there is nothing being announced to draw — so `isDown` is what the card
  // uses to replace the prefix counts and the "click to view" CTA with the down treatment.
  // Sorted with the same comparator as the live cards above: `deriveDownNeighborsWithoutRoutes`
  // already orders them, but node order is this function's contract, so it does not depend on a
  // caller having done it.
  for (const peer of [...downPeers].sort((a, b) => compareNeighborIp(a.neighborIp, b.neighborIp))) {
    if (neighborAgg.has(peer.neighborIp)) continue;

    newNodes.push({
      id: `neighbor-${peer.neighborIp}`,
      type: 'neighbor',
      position: { x: 50, y: currentY },
      data: {
        neighborIp: peer.neighborIp,
        isDown: true,
        sessionStatus: peer.sessionStatus,
        prefixCount: 0,
        routeTableCount: 0,
        bestCount: 0,
        withdrawnCount: 0,
        prefixes: []
      }
    });
    currentY += TOPOLOGY_CARD_LAYOUT.neighborCardH + TOPOLOGY_CARD_LAYOUT.cardGap;
  }

  // Cloud container (VPCs → route tables with counts; rendered by CloudContainer/VpcCard).
  let cloudLabel = 'Cloud (AWS)';
  if (selectedEntity?.account_display_name && selectedEntity?.region) {
    const accountName = selectedEntity.account_display_name.startsWith('AWS')
      ? selectedEntity.account_display_name
      : `AWS ${selectedEntity.account_display_name}`;
    cloudLabel = `${accountName} - ${selectedEntity.region}`;
  } else if (selectedEntity?.account_id && selectedEntity?.region) {
    const accountId = selectedEntity.account_id.startsWith('AWS')
      ? selectedEntity.account_id
      : `AWS ${selectedEntity.account_id}`;
    cloudLabel = `${accountId} - ${selectedEntity.region}`;
  }

  newNodes.push({
    id: 'cloud-container',
    type: 'cloud',
    position: { x: 700, y: 50 },
    data: {
      label: cloudLabel,
      vpcs: Array.from(uniqueVpcs),
      // A not-injected row names a route table AWS has no route in (CTX-738): listing it on the
      // VPC card would draw a table with prefixes that are not there.
      allData: routeRows.filter((r) => !isNotInjected(r)).map((r) => ({
        neighbor_ip: r.neighborIp,
        prefix: r.prefix,
        family: r.family,
        vpc: r.vpc,
        route_table_id: r.routeTableId,
        subnet_id: r.subnetId,
        local_pref: r.localPref,
        med: r.med,
        as_path: r.asPath,
        selected: r.isBestPath,
        withdrawn: r.withdrawn,
        // Keeps the wire name because `RouteTableModal` renders these entries as if they were
        // raw CPR entries. Omitting it is what made the modal print "Specific Next Hop: N/A" for
        // every prefix while the Table view — which reads `RouteRow.nextHop` directly — showed
        // the real ENI (CTX-207).
        specific_next_hop: r.nextHop,
        // Carried through so the VPC card can flag a tag-discovered destination and the
        // route-table / subnet modal can name the tags (CTX-129) — both read these entries,
        // in BOTH directions: outbound groups them by `subnet_id`, inbound by `route_table_id`.
        tag_attribution: r.tagAttribution
      })),
      accountId: selectedEntity?.account_display_name || selectedEntity?.account_id,
      region: selectedEntity?.region,
      headerColor: 'bg-semantic-bg-warning'
    }
  });

  const neighborSet = new Set(neighborAgg.keys());
  // Two z-order layers: subdued edges are drawn first (underneath), emphasised edges last (on top).
  const subduedEdges: Edge[] = [];
  const emphasizedEdges: Edge[] = [];

  // One aggregated edge per (neighbor, route-table), coloured purely by path status:
  // pink = best path, grey = alternate path (CTX-106 — the old per-reflector shared/unique teal
  // split is gone). In cluster mode, an edge whose destination is in `inconsistentRouteTables`
  // (the selected reflector's prefix count there differs from the other member's) gets a YELLOW
  // count badge; the line and arrowhead keep their pink/grey colour (badge-only signal).
  for (const ea of edgeAgg.values()) {
    if (!neighborSet.has(ea.neighborIp)) continue;

    const neighborNodeId = `neighbor-${ea.neighborIp}`;
    const source = isOutbound ? 'cloud-container' : neighborNodeId;
    const target = isOutbound ? neighborNodeId : 'cloud-container';
    const sourceHandle = isOutbound ? `handle-rt-${ea.handleKey}` : `neighbor-handle-${ea.neighborIp}`;
    const targetHandle = isOutbound ? `neighbor-handle-${ea.neighborIp}` : `handle-rt-${ea.handleKey}`;

    // Best path (won the BGP decision) → pink + emphasised; alternate → grey + subdued.
    const best = ea.best;
    const color = best ? PINK : GREY;
    const count = ea.prefixes.size;
    // Inconsistency (cluster): the count to this destination diverges from the other member's.
    const inconsistent = inconsistentRouteTables?.has(ea.handleKey) ?? false;
    const dashArray = best ? '10 6' : '6 4';

    const edge: Edge = {
      id: `${direction.toLowerCase()}-agg-${ea.neighborIp}-${ea.handleKey}`,
      source,
      sourceHandle,
      target,
      targetHandle,
      type: 'offsetBezier',
      label: `×${count}`,
      // Badge-only inconsistency signal: the count label turns yellow, everything else stays
      // pink/grey (CTX-106).
      labelStyle: { fill: inconsistent ? WARNING : color, fontSize: 11, fontWeight: 600 },
      labelBgStyle: { fill: 'var(--color-semantic-bg-primary)', fillOpacity: 0.85 },
      markerEnd: { type: MarkerType.ArrowClosed, width: 11, height: 11, color },
      style: {
        stroke: color,
        strokeWidth: best ? 2.5 : 2.2,
        strokeDasharray: dashArray,
        // Best paths flow (animated dash) + glow; alternates stay static and dimmed.
        ...(best ? { animation: 'flowPink 1.0s linear infinite', filter: `drop-shadow(0 0 8px ${color})`, opacity: 1 } : { opacity: 0.6 }),
        zIndex: best ? 50 : 30
      },
      data: {
        neighbor_ip: ea.neighborIp,
        routeTable: ea.handleKey,
        count,
        selected: best,
        inconsistent,
        offset: 0,
        reflectors: Array.from(ea.reflectors)
      }
    };
    (best ? emphasizedEdges : subduedEdges).push(edge);
  }

  newEdges.push(...subduedEdges, ...emphasizedEdges);

  const validNodeIds = new Set(newNodes.map((node) => node.id));
  const filteredEdges = newEdges.filter((edge) => validNodeIds.has(edge.source) && validNodeIds.has(edge.target));

  return { nodes: newNodes, edges: filteredEdges };
};
